import { useState } from 'react';

const entryService = {
  id: 1,
  title: 'Stability Signature',
  subtitle: 'Consultation · Acupuncture · Advanced Facial Integration',
  tagline: 'Your entry into the Vionne Stability Method.',
  description: `When the nervous system carries a sustained load, the face begins to reflect it.
This 90-minute session evaluates those patterns and initiates structural regulation through acupuncture and advanced facial integration.`,
  pinnedNote:
    'New clients begin with the Stability Signature so that facial patterns, nervous system load, and structural tension can be properly understood before continuing with follow-up care.',
  duration: '90 min',
  price: '$320',
  includes: [
    'Comprehensive facial mapping and assessment',
    'Full‑body acupuncture treatment',
    'Advanced facial acupuncture and tension release',
    'Personalized treatment and homecare plan',
  ],
  focus: 'Nervous system overload · Hormonal imbalance · Jaw tension · Structural facial vitality',
  image:
    'https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/1e62d1ee0ce9a670d308145d38d331be.png',
  clinicalOutcomes: {
    observed: [
      'Reduced jaw clenching and decreased facial tension patterns',
      'Improved facial symmetry and contour definition',
      'Enhanced micro‑circulation and under‑eye brightness',
      'More regulated sleep patterns and stress response',
      'Improved autonomic steadiness reflected in facial tone',
    ],
    reported: [
      'Feeling healthier and less internally strained',
      'More consistent energy throughout the week',
      'Greater composure in high‑pressure environments',
      'A renewed sense of quiet confidence — both in presence and performance',
    ],
  },
};

const clinicalTreatments = [
  {
    id: 5,
    title: 'ElectroSonic Stability Sculpt',
    subtitle: 'Focused Sonic Energy · ElectroMuscle Stimulation · Botanical Neuromodulation',
    tagline: 'Four systems. Two precise technologies. One structural session.',
    description:
      "Grounded in acupuncture‑based regulation, this advanced sculpting treatment integrates electrosonic technology and botanical support to release structural tension, refine facial contours, and restore dynamic circulation.",
    duration: '75 min',
    price: '$295',
    focus: 'Cheek lifting · Nasolabial softening · Jawline definition · Systemic regulation',
    includes: [
      'Focused sonic SMAS fascia lifting',
      'ElectroMuscle stimulation along meridian channels',
      'Botanical Neuromodulation integration',
      'Personalised structural protocol',
    ],
    image:
      'https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/6e5cb940827943aec80e01aa7d1d2458.png',
    modalities: ['Hifu Ultrasonic', 'EMS', 'Botanical Acupuncture'],
    needleFreeNote: null,
  },
  {
    id: 4,
    title: 'Aqua Botanical Infusion',
    subtitle: 'Needle‑Free Dermal Infusion · Botanical Depth',
    tagline: 'Needle‑free dermal infusion. Botanical depth.',
    description:
      "Rooted in acupuncture therapy, this restorative treatment combines facial infusion and concentrated botanical actives to deeply replenish hydration, calm reactive skin, and strengthen barrier resilience.",
    duration: '75 min',
    price: '$275',
    focus: 'Dermal hydration · Visible plumpness · Skin receptivity · Structural reinforcement',
    includes: [
      'Italian DEP device botanical infusion',
      'Collagen firming mask',
      'Body acupuncture integration',
      'Personalised botanical protocol',
    ],
    image:
      'https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/af6e49cd77a6b27cb2cca24f896d53b4.png',
    modalities: ['Needle‑free Mesotherapy', 'Botanical Acupuncture'],
    needleFreeNote: null,
  },
  {
    id: 2,
    title: 'Stability Acupuncture',
    subtitle: 'Botanical Acupuncture Treatment',
    tagline: 'Body and facial acupuncture working in concert.',
    description:
      'A facial‑focused acupuncture treatment designed to regulate nervous system patterns, improve systemic circulation, and restore structural vitality reflected across the face.',
    duration: '75 min',
    price: '$255',
    focus: 'Stress · Sleep · Hormonal regulation · Pain · Digestive concerns',
    includes: [
      'Full botanical acupuncture treatment',
      'Meridian assessment',
      'Botanical Neural Modulation integration',
      'Personalized homecare plan',
    ],
    image:
      'https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/28fa5959b6850ce1f57d68311dcd1800.png',
    modalities: ['Facial Acupuncture', 'Botanical Acupuncture', 'Qi Beauty'],
    needleFreeNote:
      'Needle‑free option available — Facial acupuncture may be substituted with Qi Beauty, a micro‑magnet therapy that stimulates the same meridian points without needles.',
  },
];

const maintenanceService = {
  id: 3,
  title: 'Botanical Regulation Facial',
  subtitle: 'Botanical Facial Regulation · Structural Refinement',
  tagline: 'Skin clarity, guided by your facial archetype.',
  description:
    'An acupuncture‑based botanical facial focused on calming internal load, maintaining skin balance, and supporting visible stability between clinical treatments. Grounded in your individual Facial Archetype, this treatment works through the full range of skin concerns.',
  duration: '65 min',
  price: '$220',
  focus: 'Congestion · Acne · Enlarged pores · Dehydration · Fine lines · Puffiness · Under‑eye changes',
  includes: [
    'Deep cleanse & exfoliation',
    'Active infusion & mask',
    'Facial massage & tension release',
    'Supporting body acupuncture',
    'Personalized facial protocol',
  ],
  image:
    'https://static.readdy.ai/image/296ab9838c8a2f1e2b2bac03a156c5b2/617565010ae028b9fc967d675e196e66.png',
  modalities: ['Regulation Facial', 'Acupuncture', 'Botanical Neuromodulation'],
};

const enhancementServices = [
  {
    id: 6,
    title: 'Hot Stone Acupressure',
    subtitle: 'Thermal Meridian Therapy · Deep Tissue Release',
    tagline: 'Warmth that moves through the meridians.',
    description:
      'Heated basalt stones are placed along key meridian pathways to dissolve deep muscular tension, stimulate circulation, and restore thermal balance to the nervous system. A grounding add‑on that amplifies the effects of any clinical treatment.',
    duration: '30 min',
    price: '$65',
    icon: 'ri-fire-line',
  },
  {
    id: 7,
    title: 'Botanical Cocktail Formulation',
    subtitle: 'Bespoke Botanical Prescription · Homecare Protocol',
    tagline: 'A formula made for your nervous system, not just your skin.',
    description:
      'A personalized 30 ml botanical serum or products formulated based on your Stability Facial Archetype and systemic assessment. Designed to extend the clinical work between sessions and support your skin’s regulatory capacity at home.',
    duration: '30 min',
    price: '$88',
    icon: 'ri-flask-line',
  },
];

const ServicesGrid = () => {
  const [expandedEntry, setExpandedEntry] = useState(false);
  const [expandedClinical, setExpandedClinical] = useState<number | null>(null);
  const [expandedMaintenance, setExpandedMaintenance] = useState(false);

  const navigate = (path: string) => {
    try {
      if (typeof window !== 'undefined' && typeof (window as any).REACT_APP_NAVIGATE === 'function') {
        (window as any).REACT_APP_NAVIGATE(path);
      } else {
        window.location.href = path;
      }
    } catch (e) {
      console.error('Navigation error:', e);
    }
  };

  return (
    <section
      id="services-grid"
      className="py-10 sm:py-16 px-4 sm:px-6 lg:px-12 bg-warmWhite"
    >
      <div className="max-w-6xl mx-auto">

        {/* Section Header */}
        <div className="mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-8 h-[1px] bg-bronze"></div>
            <span className="text-bronze text-[8px] font-sans font-semibold tracking-[0.4em] uppercase">
              Clinical Offerings
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-light text-charcoal italic leading-tight">
              Our Stability Services
            </h2>
            <p className="text-charcoal/70 text-[12px] sm:text-[13px] font-sans font-light leading-loose max-w-[380px] lg:text-right tracking-wide">
              Each session is a conversation between your nervous system<br className="hidden lg:block" /> and your structural expression.
            </p>
          </div>
        </div>

        {/* ── ZONE A: ENTRY POINT ── */}
        <div className="mb-6 sm:mb-8">
          {/* Step label */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex items-baseline gap-2 group cursor-default transition-transform duration-300 ease-out hover:scale-[1.2] origin-left">
              <span className="text-[11px] font-sans font-bold tracking-[0.35em] uppercase text-bronze">01</span>
              <span className="text-[11px] font-sans font-semibold tracking-[0.25em] uppercase text-bronze/80">Begin Here</span>
            </div>
            <div className="flex-1 h-[1px] bg-gradient-to-r from-bronze/30 to-transparent"></div>
          </div>

          {/* Entry card */}
          <div className="relative bg-white overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.04)' }}>
            <div className="grid grid-cols-1 lg:grid-cols-5">
              {/* Image */}
              <div className="lg:col-span-2 relative h-[280px] sm:h-[380px] lg:h-auto lg:min-h-[520px] overflow-hidden">
                <img
                  src={entryService.image}
                  alt={entryService.title}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/5"></div>
              </div>

              {/* Content */}
              <div className="lg:col-span-3 p-6 sm:p-8 lg:p-10 flex flex-col justify-between min-h-[420px]">
                <div>
                  {/* Top row: tag + price/duration */}
                  <div className="flex items-start justify-between mb-6">
                    <span className="inline-flex items-center px-3 py-1 text-[8px] font-sans font-semibold tracking-[0.3em] uppercase text-bronze border border-bronze/30 w-fit">
                      Foundation Session
                    </span>
                    <div className="text-right flex-shrink-0 ml-4">
                      <span className="text-charcoal/50 text-[9px] font-sans font-light block tracking-widest uppercase mb-0.5">{entryService.duration}</span>
                      <span className="text-charcoal text-xl font-display font-light">{entryService.price}</span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="font-display text-2xl sm:text-3xl font-light text-charcoal italic mb-2 leading-tight">
                      {entryService.title}
                    </h3>
                    <p className="text-charcoal/50 text-[9px] font-sans tracking-[0.25em] uppercase leading-relaxed">
                      {entryService.subtitle}
                    </p>
                  </div>

                  <p className="text-charcoal/80 text-[13px] sm:text-[14px] font-display font-light italic mb-4 leading-relaxed">
                    "{entryService.tagline}"
                  </p>

                  <p className="text-charcoal/85 text-[11px] sm:text-[12px] font-sans font-light leading-loose mb-5 max-w-md">
                    {entryService.description}
                  </p>

                  <div className="relative pl-4 mb-5">
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-bronze to-bronze/20"></div>
                    <p className="text-[10px] sm:text-[11px] font-sans font-light text-charcoal/80 leading-loose italic">
                      {entryService.pinnedNote}
                    </p>
                  </div>

                  <button
                    onClick={() => setExpandedEntry(!expandedEntry)}
                    className="group flex items-center gap-3 text-charcoal/50 hover:text-charcoal text-[10px] font-sans tracking-[0.2em] uppercase transition-colors duration-300 mb-4 cursor-pointer"
                  >
                    <span>{expandedEntry ? 'Conceal' : 'Reveal full details'}</span>
                    <div className="w-4 h-[1px] bg-current transition-all duration-300 group-hover:w-6"></div>
                    <i className={`ri-arrow-${expandedEntry ? 'up' : 'down'}-s-line text-xs`}></i>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-700 ${
                      expandedEntry ? 'max-h-[2000px] opacity-100 mb-8' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="border-t border-charcoal/6 pt-7 mb-7">
                      <span className="text-[8px] font-sans font-semibold text-charcoal/60 tracking-[0.35em] uppercase block mb-6">
                        What begins to shift
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        <div>
                          <p className="text-[8px] font-sans font-semibold text-bronze/70 tracking-[0.3em] uppercase mb-4">
                            Across 5–6 sessions, we observe:
                          </p>
                          <ul className="space-y-2.5">
                            {entryService.clinicalOutcomes.observed.map((item) => (
                              <li key={item} className="flex items-start gap-3">
                                <span className="text-bronze/50 text-[9px] flex-shrink-0 mt-1">—</span>
                                <span className="text-[10px] sm:text-[11px] font-sans font-light text-charcoal/80 leading-relaxed">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-[8px] font-sans font-semibold text-bronze/70 tracking-[0.3em] uppercase mb-4">
                            Clients frequently report:
                          </p>
                          <ul className="space-y-2.5">
                            {entryService.clinicalOutcomes.reported.map((item) => (
                              <li key={item} className="flex items-start gap-3">
                                <span className="text-bronze/50 text-[9px] flex-shrink-0 mt-1">—</span>
                                <span className="text-[10px] sm:text-[11px] font-sans font-light text-charcoal/80 leading-relaxed">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-charcoal/6 pt-7 mb-7">
                      <span className="text-[8px] font-sans font-semibold text-charcoal/50 tracking-[0.35em] uppercase block mb-4">
                        Session Includes
                      </span>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-8">
                        {entryService.includes.map((item) => (
                          <li key={item} className="flex items-start gap-2.5">
                            <span className="text-bronze/50 text-[9px] flex-shrink-0 mt-0.5">◦</span>
                            <span className="text-[10px] font-sans font-light text-charcoal/80 leading-relaxed">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="border-t border-charcoal/6 pt-7">
                      <span className="text-[8px] font-sans font-semibold text-charcoal/50 tracking-[0.35em] uppercase block mb-3">
                        Recommended Rhythm
                      </span>
                      <p className="text-[11px] font-sans font-light text-charcoal/80 leading-loose">
                        Structural recalibration is progressive.<br />
                        Most clients begin noticing subtle shifts within the first 2–3 sessions, while deeper stabilization typically develops across 6–8 sessions, followed by refinement and maintenance sessions to sustain results.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <button
                    onClick={() => navigate('/booking')}
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-charcoal text-white text-[10px] font-sans font-semibold tracking-[0.25em] uppercase hover:bg-charcoal/80 transition-all duration-400 whitespace-nowrap cursor-pointer"
                  >
                    <span>Reserve Your Session</span>
                    <i className="ri-arrow-right-line text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── ZONE B: TREATMENT PATH ── */}
        <div className="mt-14 sm:mt-20">
          {/* Step label */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex items-baseline gap-2 group cursor-default transition-transform duration-300 ease-out hover:scale-[1.2] origin-left">
              <span className="text-[11px] font-sans font-semibold tracking-[0.35em] uppercase text-charcoal/70">02</span>
              <span className="text-[11px] font-sans font-semibold tracking-[0.25em] uppercase text-charcoal/60">Your Treatment Path</span>
            </div>
            <div className="flex-1 h-[1px] bg-gradient-to-r from-charcoal/20 to-transparent"></div>
          </div>

          <p className="text-charcoal/80 text-[12px] sm:text-[13px] font-sans font-light leading-loose mb-10 max-w-lg tracking-wide">
            Following your Stability Signature, your practitioner will design a personalised protocol from the treatments below — tailored to your concerns and goals.
          </p>

          {/* Sub‑category: Clinical Treatments */}
          <div className="mb-10">
            <div className="flex items-center gap-4 mb-8">
              <span className="text-[9px] font-sans font-semibold tracking-[0.35em] uppercase text-bronze/80">
                Core Regulation
              </span>
              <div className="flex-1 h-[1px] bg-gradient-to-r from-bronze/20 to-transparent"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-[1px] bg-[#e8dfd5]">
              {clinicalTreatments.map((service, index) => {
                const isExpanded = expandedClinical === index;
                const numerals = ['I', 'II', 'III'];
                return (
                  <div key={service.id} className="bg-[#fdfcfb] flex flex-col group">
                    <div className="relative h-[220px] sm:h-[260px] overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-1000 group-hover:scale-103"
                        style={{ transform: isExpanded ? 'scale(1)' : undefined }}
                      />
                      <div className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center">
                        <span className="text-white/60 text-[10px] font-display font-light italic">{numerals[index]}</span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#fdfcfb]/60 to-transparent"></div>
                    </div>

                    <div className="p-6 sm:p-7 flex flex-col flex-1">

                      <div className="mb-4">
                        <h3 className="font-display text-xl sm:text-2xl font-light text-charcoal italic mb-1.5 leading-snug">
                          {service.title}
                        </h3>
                        <p className="text-charcoal/50 text-[8px] font-sans tracking-[0.2em] uppercase leading-relaxed">
                          {service.subtitle}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 mb-6">
                        <span className="text-charcoal/60 text-[10px] font-sans font-light">{service.duration}</span>
                        <span className="text-charcoal/20 text-[10px]">·</span>
                        <span className="text-charcoal text-[15px] font-display font-light">{service.price}</span>
                      </div>

                      <p className="text-charcoal/75 text-[11px] font-display font-light italic mb-3 leading-relaxed">
                        "{service.tagline}"
                      </p>

                      <p className="text-charcoal/85 text-[11px] font-sans font-light leading-loose mb-4 flex-1">
                        {service.description}
                      </p>

                      {service.needleFreeNote && (
                        <div className="relative pl-4 mb-4">
                          <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-charcoal/20"></div>
                          <p className="text-[9px] font-sans font-light text-charcoal/60 italic leading-relaxed">
                            {service.needleFreeNote}
                          </p>
                        </div>
                      )}

                      <button
                        onClick={() => setExpandedClinical(isExpanded ? null : index)}
                        className="group/btn flex items-center gap-2.5 text-charcoal/50 hover:text-charcoal/80 text-[9px] font-sans tracking-[0.2em] uppercase mb-4 cursor-pointer transition-colors duration-200"
                      >
                        <span>{isExpanded ? 'Conceal' : 'Reveal details'}</span>
                        <div className="w-3 h-[1px] bg-current transition-all duration-300 group-hover/btn:w-5"></div>
                        <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-[10px]`}></i>
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-600 ${
                          isExpanded ? 'max-h-[700px] opacity-100 mb-5' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="border-t border-charcoal/6 pt-5 mb-4">
                          <span className="text-[7px] font-sans font-semibold text-charcoal/50 tracking-[0.35em] uppercase block mb-2">
                            Session Includes
                          </span>
                          <ul className="space-y-2">
                            {service.includes.map((item) => (
                              <li key={item} className="flex items-start gap-2.5">
                                <span className="text-bronze/50 text-[9px] flex-shrink-0 mt-0.5">◦</span>
                                <span className="text-[10px] font-sans font-light text-charcoal/80 leading-relaxed">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="border-t border-charcoal/6 pt-4">
                          <span className="text-[7px] font-sans font-semibold text-charcoal/50 tracking-[0.35em] uppercase block mb-1">
                            Clinical Focus
                          </span>
                          <p className="text-[10px] font-sans font-light text-charcoal/70 leading-relaxed italic">
                            {service.focus}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sub‑category: Maintenance */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-baseline gap-2 group cursor-default transition-transform duration-300 ease-out hover:scale-[1.2] origin-left">
                <span className="text-[11px] font-sans font-semibold tracking-[0.35em] uppercase text-charcoal/70">03</span>
                <span className="text-[11px] font-sans font-semibold tracking-[0.25em] uppercase text-charcoal/60">Maintenance</span>
              </div>
              <div className="flex-1 h-[1px] bg-gradient-to-r from-charcoal/15 to-transparent"></div>
            </div>

            <div className="relative bg-[#fdfcfb] overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.03), 0 4px 20px rgba(0,0,0,0.03)' }}>
              <div className="grid grid-cols-1 lg:grid-cols-5">
                <div className="lg:col-span-2 relative h-[260px] lg:h-auto lg:min-h-[360px] overflow-hidden">
                  <img
                    src={maintenanceService.image}
                    alt={maintenanceService.title}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#fdfcfb]/10"></div>
                </div>

                <div className="lg:col-span-3 p-8 sm:p-10 lg:p-12 flex flex-col justify-between">
                  <div>

                    <div className="mb-5">
                      <h3 className="font-display text-2xl sm:text-3xl font-light text-charcoal italic mb-2 leading-tight">
                        {maintenanceService.title}
                      </h3>
                      <p className="text-charcoal/50 text-[8px] font-sans tracking-[0.2em] uppercase">
                        {maintenanceService.subtitle}
                      </p>
                    </div>

                    <div className="flex items-center gap-3 mb-6">
                      <span className="text-charcoal/60 text-[10px] font-sans font-light">{maintenanceService.duration}</span>
                      <span className="text-charcoal/20 text-[10px]">·</span>
                      <span className="text-charcoal text-xl font-display font-light">{maintenanceService.price}</span>
                    </div>

                    <p className="text-charcoal/80 text-[13px] font-display font-light italic mb-4 leading-relaxed">
                      "{maintenanceService.tagline}"
                    </p>

                    <p className="text-charcoal/85 text-[12px] font-sans font-light leading-loose mb-6 max-w-md">
                      {maintenanceService.description}
                    </p>

                    <button
                      onClick={() => setExpandedMaintenance(!expandedMaintenance)}
                      className="group/btn flex items-center gap-2.5 text-charcoal/50 hover:text-charcoal/80 text-[9px] font-sans tracking-[0.2em] uppercase mb-5 cursor-pointer transition-colors duration-200"
                    >
                      <span>{expandedMaintenance ? 'Conceal' : 'Reveal details'}</span>
                      <div className="w-3 h-[1px] bg-current transition-all duration-300 group-hover/btn:w-5"></div>
                      <i className={`ri-arrow-${expandedMaintenance ? 'up' : 'down'}-s-line text-[10px]`}></i>
                    </button>

                    <div
                      className={`overflow-hidden transition-all duration-700 ${
                        expandedMaintenance ? 'max-h-[600px] opacity-100 mb-6' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="border-t border-charcoal/6 pt-6 mb-5">
                        <span className="text-[7px] font-sans font-semibold text-charcoal/50 tracking-[0.35em] uppercase block mb-3">
                          Session Includes
                        </span>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-8">
                          {maintenanceService.includes.map((item) => (
                            <li key={item} className="flex items-start gap-2.5">
                              <span className="text-bronze/50 text-[9px] flex-shrink-0 mt-0.5">◦</span>
                              <span className="text-[10px] font-sans font-light text-charcoal/80 leading-relaxed">
                                {item}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="border-t border-charcoal/6 pt-5">
                        <span className="text-[7px] font-sans font-semibold text-charcoal/50 tracking-[0.35em] uppercase block mb-1">
                          Clinical Focus
                        </span>
                        <p className="text-[10px] font-sans font-light text-charcoal/70 leading-relaxed italic">
                          {maintenanceService.focus}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <button
                      onClick={() => navigate('/booking')}
                      className="group/cta inline-flex items-center gap-3 text-charcoal/50 hover:text-charcoal text-[9px] font-sans tracking-[0.2em] uppercase transition-colors duration-300 cursor-pointer"
                    >
                      <span>Reserve</span>
                      <div className="w-5 h-[1px] bg-current transition-all duration-300 group-hover/cta:w-8"></div>
                      <i className="ri-arrow-right-line text-[10px] transition-transform duration-300 group-hover/cta:translate-x-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── ZONE C: ENHANCEMENT ── */}
        <div className="mt-14 sm:mt-20 flex items-center gap-6">
          <div className="flex-1 h-[1px] bg-charcoal/6"></div>
          <span className="text-charcoal/20 text-[8px] font-sans tracking-[0.4em] uppercase">Vionne Stability Method</span>
          <div className="flex-1 h-[1px] bg-charcoal/6"></div>
        </div>

      </div>
    </section>
  );
};

export default ServicesGrid;